import AddIcon from '@mui/icons-material/Add';
import Fab from '@mui/material/Fab';
import { Box } from '@mui/system';
import { generateActionTask } from '@utils/flows';
import { cloneDeep } from 'lodash';
import React, {
	BaseSyntheticEvent,
	useCallback,
	useMemo,
	useRef,
	useState
} from 'react';
import { IFlowScreens, IFlowsMetadata, Task, TaskType } from 'types/flows';
import {
	ELSE_INTERACTION_ID,
	newCondition
} from '../TaskConditionsDialog/helpers';
import AddActionButton from './AddActionButton';
import AddConditionButton from './AddConditionButton';
import AddScreenButton from './AddScreenButton';
import AddTaskMenu from './AddTaskMenu';
import FlowViewAddTaskDialog from './FlowViewAddTaskDialog';
import { getSnippets } from './Snippets/utils';

interface IAddTaskButtonProps {
	metadata?: IFlowsMetadata;
	onAddTasks: (
		tasks: Task[],
		e: React.MouseEvent,
		screens?: IFlowScreens
	) => void;
}

const MENU_ITEM_WIDTH = 177;

const AddTaskButton = ({ onAddTasks, metadata }: IAddTaskButtonProps) => {
	const boxRef = useRef<HTMLDivElement>();
	const [showAddMenu, setShowAddMenu] = useState(false);
	const [showActionsDialog, setShowActionsDialog] = useState(false);

	const { actions } = metadata || {};
	const handleClose = useCallback(() => {
		setShowAddMenu(false);
	}, []);

	const handleClickAdd = useCallback(
		(e: BaseSyntheticEvent) => {
			e.stopPropagation();
			setShowAddMenu(true);
		},
		[setShowAddMenu]
	);

	const actionsOptions = Object.values(actions || {}).map((action) => ({
		id: action.name,
		title: action.displayName,
		description: action.description,
		hidden: !!action.hidden,
		tags: action.tags
	}));

	const onAddAction = useCallback(
		(id: string, e: React.MouseEvent) => {
			const action = actions?.[id];
			onAddTasks([generateActionTask(id, action)], e);
			setShowActionsDialog(false);
		},
		[setShowActionsDialog, onAddTasks, actions]
	);

	const onAddCondition = useCallback(
		(e: React.MouseEvent) => {
			onAddTasks(
				[
					{
						name: 'Condition',
						type: TaskType.condition,
						conditions: [
							newCondition('if'),
							{
								interactionId: ELSE_INTERACTION_ID,
								name: ELSE_INTERACTION_ID,
								atomicConditions: []
							}
						]
					}
				],
				e
			);
		},
		[onAddTasks]
	);
	const snippets = useMemo(() => getSnippets(actions), [actions]);

	const onAddSnippet = useCallback(
		(id: string, e: React.MouseEvent) => {
			const selectedSnippet = snippets.find((snippet) => snippet.id === id);
			if (selectedSnippet) {
				onAddTasks(
					cloneDeep(selectedSnippet.tasks),
					e,
					cloneDeep(selectedSnippet.screens)
				);
				setShowActionsDialog(false);
			}
		},
		[setShowActionsDialog, onAddTasks, snippets]
	);

	return (
		<Box
			ref={boxRef}
			sx={{ position: 'absolute', left: 15, bottom: 0, zIndex: 5 }}
		>
			{showActionsDialog && (
				<FlowViewAddTaskDialog
					onCancel={() => setShowActionsDialog(false)}
					onDone={onAddAction}
					title="action"
					options={actionsOptions}
					snippets={snippets}
					onSnippetDone={onAddSnippet}
				/>
			)}
			<AddTaskMenu
				addButtonRef={boxRef.current}
				isOpen={showAddMenu}
				onClick={handleClose}
			>
				<AddScreenButton width={MENU_ITEM_WIDTH} onAddTasks={onAddTasks} />
				<AddActionButton
					width={MENU_ITEM_WIDTH}
					onClick={() => {
						setShowActionsDialog(true);
					}}
				/>
				<AddConditionButton width={MENU_ITEM_WIDTH} onClick={onAddCondition} />
			</AddTaskMenu>
			<Fab
				color="primary"
				aria-label="add"
				onClick={handleClickAdd}
				data-testid="flow-add-task-button"
				sx={{
					marginBottom: 2,
					'&:hover': { transform: 'rotate(90deg)' },
					transition: 'all 0.3s ease-in-out',
					transform: 'rotate(0deg)'
				}}
			>
				<AddIcon fontSize="large" />
			</Fab>
		</Box>
	);
};

AddTaskButton.defaultProps = {
	metadata: undefined
};

export default AddTaskButton;
